import React from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { isRecoverableChunkError } from './router/lazyWithRetry'

type AppErrorBoundaryProps = {
  children: React.ReactNode
}

type AppErrorBoundaryState = {
  error: Error | null
}

export class AppErrorBoundary extends React.Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  public state: AppErrorBoundaryState = {
    error: null,
  }

  public static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error }
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('[AppErrorBoundary] App bootstrap failed', error, errorInfo)
  }

  private handleReload = (): void => {
    window.location.reload()
  }

  public render(): React.ReactNode {
    if (!this.state.error) {
      return this.props.children
    }

    const isChunkError = isRecoverableChunkError(this.state.error)
    const supportMessage = isChunkError
      ? '動的インポートの読込に失敗しました。開発中の古い chunk やブラウザ session が残っている可能性があります。自動再読み込みを1回試した後も失敗した場合は、開発サーバーを再起動してください。'
      : '画面の初期化中にエラーが発生しました。再読み込みしても解消しない場合は、開発サーバーのログとブラウザの Console を確認してください。'

    return (
      <div className="min-h-screen bg-background px-4 py-10 text-foreground">
        <div className="mx-auto flex min-h-[80vh] max-w-xl items-center justify-center">
          <Card className="w-full">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="h-8 w-8 text-destructive" />
              </div>
              <CardTitle className="text-2xl font-semibold">
                アプリの起動に失敗しました
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-center">
              <p className="text-sm text-muted-foreground">
                {supportMessage}
              </p>
              <p className="rounded bg-muted p-3 text-left font-mono text-xs text-muted-foreground">
                {this.state.error.message}
              </p>
              <div className="flex justify-center">
                <Button onClick={this.handleReload} variant="outline">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  再読み込み
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }
}
